import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Loader2, ArrowRight, ShieldCheck, ChevronDown, Sparkles, Megaphone, Clock, AlertTriangle, ExternalLink, MessageCircle, Instagram, Gift, Settings2 } from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/site/SEOHead";
import FAQSection from "@/components/site/FAQSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CLIENT_URL = (process.env.REACT_APP_CLIENT_URL || "").trim();

// --- Contact secondaire ---------------------------------------------------
const WHATSAPP_NUMBER = "262693482110"; // EM Group Réunion
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Bonjour EM Group, je souhaite en savoir plus sur l'offre découverte réseaux sociaux."
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const WHATSAPP_CUSTOM_MESSAGE = encodeURIComponent(
  "Bonjour EM Group, je souhaite discuter d'une offre personnalisée pour mon entreprise."
);
const WHATSAPP_CUSTOM_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_CUSTOM_MESSAGE}`;

const INSTAGRAM_USERNAME = "emgroup.digital";
const INSTAGRAM_URL = `https://instagram.com/${INSTAGRAM_USERNAME}`;

const REFERRER_OPTIONS = ["Facebook", "Instagram", "Google", "Bouche à oreille", "Autre"];

// --- SEO -------------------------------------------------------------------
const CANONICAL = "https://emgroupdigital.fr/decouverte-rs";
const TITLE = "Gestion réseaux sociaux La Réunion — 2 mois offerts | EM Group";
const DESCRIPTION =
  "Gestion réseaux sociaux à La Réunion : 2 mois offerts, publications + visuels + 1 campagne Meta Ads incluse. Community manager 974. Places limitées.";
const KEYWORDS =
  "gestion réseaux sociaux La Réunion, community manager Réunion, agence réseaux sociaux 974, gestion Facebook Instagram Réunion, agence social media Réunion, EM Group";

const LOCAL_BUSINESS = {
  name: "EM Group",
  description:
    "Agence de gestion des réseaux sociaux à La Réunion : community management, création de contenus, campagnes Meta Ads et accompagnement social media pour TPE/PME.",
  email: "contact@emgroupdigital.fr",
  url: CANONICAL,
  image: "https://emgroupdigital.fr/brand/logo-em-group.png",
  areaServed: ["La Réunion", "974", "France"],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Saint-Denis",
    addressRegion: "La Réunion",
    postalCode: "97400",
    addressCountry: "RE",
  },
};

const SERVICE_SCHEMA = {
  name: "Gestion des réseaux sociaux à La Réunion",
  serviceType: "Community management & social media",
  description:
    "Gestion de vos réseaux sociaux à La Réunion : publications, visuels, community management, campagnes Meta Ads. Offre découverte 2 mois offerts, sans engagement.",
  areaServed: "La Réunion",
  price: "450",
  priceCurrency: "EUR",
  priceDescription: "À partir de 450€ TTC/mois après les 2 mois découverte offerts.",
};

const FAQ_ITEMS = [
  {
    q: "Comment fonctionne l'offre découverte 2 mois offerts ?",
    a: "Vous choisissez votre formule de poursuite (Essentiel 450€ TTC/mois ou Pro 750€ TTC/mois) puis vous bénéficiez de 2 mois entièrement offerts : publications, visuels, rédactions des contenus, planification + une campagne Meta Ads incluse (création, paramétrage et suivi). Votre formule choisie démarre automatiquement au mois 3, avec un engagement minimum d'un seul mois.",
  },
  {
    q: "Le budget publicitaire est-il inclus ?",
    a: "Non — la création, le paramétrage et le suivi de votre campagne Meta Ads sont inclus dans l'offre découverte (prestation pouvant valoir jusqu'à 1 000€ ailleurs). En revanche, le budget publicitaire est payé directement par vous à Meta depuis votre Business Manager (minimum recommandé : 100€/mois). EM Group ne perçoit aucune commission sur votre budget pub.",
  },
  {
    q: "Que se passe-t-il après les 2 mois offerts ?",
    a: "Votre formule (Essentiel ou Pro) démarre automatiquement avec un engagement minimum d'1 seul mois payant. Passé ce premier mois, votre abonnement est résiliable à tout moment avec un préavis de 30 jours calendaires, par simple email à contact@emgroupdigital.fr. Un passage vers la formule supérieure est possible à tout moment sur demande.",
  },
];

function fireLeadConversion(value = 50) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", { send_to: "AW-18201979242/kUtFCJzRrrccEOrSsOdD", value, currency: "EUR" });
  }
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", { content_name: "Découverte RS", value, currency: "EUR" });
  }
}

const INCLUSIONS = [
  { ok: true, text: "Publications et visuels (selon formule choisie)" },
  { ok: true, text: "Rédaction des contenus" },
  { ok: true, text: "Mise en place d'1 campagne Meta Ads (setup inclus)" },
  { ok: true, text: "Landing page dédiée à la campagne si nécessaire" },
  { ok: false, text: "Budget publicitaire non inclus — vous choisissez votre budget (minimum recommandé : 100€/mois)" },
];

const PLANS = [
  {
    key: "essentiel", name: "Essentiel", price: "450€", priceUnit: "TTC/mois",
    features: [
      { ok: true, text: "3 posts/mois" },
      { ok: true, text: "Rédaction des légendes" },
      { ok: true, text: "Planification et publication" },
      { ok: true, text: "Rapport mensuel simplifié" },
      { ok: true, text: "Délai de réponse : 48h" },
      { ok: false, text: "Stories" },
      { ok: false, text: "Optimisation profil/bio" },
      { ok: false, text: "Community management" },
      { ok: false, text: "Appel mensuel" },
    ],
  },
  {
    key: "pro", name: "Pro", price: "750€", priceUnit: "TTC/mois", highlight: true, badge: "Le plus choisi",
    features: [
      { ok: true, text: "6 posts/mois" },
      { ok: true, text: "Rédaction des légendes" },
      { ok: true, text: "Planification et publication" },
      { ok: true, text: "2 stories/mois" },
      { ok: true, text: "Optimisation profil/bio mensuelle" },
      { ok: true, text: "Community management 5j/7" },
      { ok: true, text: "Rapport mensuel détaillé + recommandations" },
      { ok: true, text: "Appel suivi mensuel 30 min" },
      { ok: true, text: "Délai de réponse : 24h" },
    ],
  },
];

const SECTORS = ["Restaurant/Café", "Commerce", "Artisan", "Services", "Hôtel/Hébergement", "Médical/Bien-être", "Autre"];

const CGV_ARTICLES = [
  { n: 1, title: "Objet", body: "Les présentes Conditions Générales de Vente (CGV) régissent l'offre découverte proposée par EM Group Digital (ci-après « EM Group ») à toute entreprise souhaitant bénéficier d'un accompagnement en gestion des réseaux sociaux et publicité digitale." },
  { n: 2, title: "Durée et démarrage", body: "L'offre découverte couvre une période de 60 jours calendaires à compter de la publication du premier contenu.\n\nLe client s'engage à fournir les éléments nécessaires (visuels, informations sur l'entreprise, accès aux comptes réseaux sociaux) dans les 15 jours suivant la signature.\n\nEn cas de retard de transmission des éléments, le démarrage est reporté dans la limite de 30 jours calendaires supplémentaires. Au-delà, l'offre découverte est considérée comme annulée sans recours possible." },
  { n: 3, title: "Choix de la formule", body: "Le client choisit sa formule de poursuite (Essentiel 450€ TTC/mois ou Pro 750€ TTC/mois) avant le démarrage de l'offre découverte.\n\nUn passage vers la formule supérieure est possible à tout moment sur simple demande.\n\nUn retour vers la formule inférieure n'est pas possible en cours d'engagement." },
  { n: 4, title: "Conditions de paiement", body: "Le client renseigne ses coordonnées bancaires lors de l'inscription via notre partenaire de paiement sécurisé Stripe.\n\nAucun prélèvement n'est effectué pendant les 60 premiers jours (période découverte).\n\nÀ l'issue de cette période, l'abonnement choisi est activé automatiquement par prélèvement mensuel récurrent sur la carte enregistrée.\n\nLe client est notifié par email 7 jours avant le premier prélèvement." },
  { n: 5, title: "Validation des contenus", body: "Chaque visuel ou contenu est soumis à validation du client par email ou messagerie.\n\nLe client dispose de 48 heures ouvrées pour valider ou demander des modifications.\n\nSans retour dans ce délai, le contenu est considéré comme validé et publié tel quel.\n\n2 retouches maximum sont incluses par visuel. Toute retouche supplémentaire est facturée séparément sur devis." },
  { n: 6, title: "Propriété des visuels", body: "Les visuels et contenus créés pendant l'offre découverte et le premier mois d'abonnement payant sont transmis au client sur simple demande écrite à l'issue de ce premier mois payant.\n\nEn cas d'arrêt de la collaboration avant ce premier mois payant, les visuels restent propriété d'EM Group Digital." },
  { n: 7, title: "Formats et contenus vidéo", body: "Les formats inclus dans l'offre sont : visuels photo et carrousels.\n\nLes stories (formule Pro uniquement, 2/mois) sont réalisées sous forme de visuels animés par EM Group, ou à partir de vidéos fournies par le client prêtes à l'emploi (format vertical 9:16, durée maximale 60 secondes, qualité suffisante pour la diffusion).\n\nToute vidéo non conforme aux spécifications techniques Meta sera refusée. La mise aux normes ou le montage vidéo font l'objet d'un devis séparé.\n\nLa création vidéo from scratch (tournage, montage complet) n'est pas incluse dans l'offre." },
  { n: 8, title: "Campagne Meta Ads", body: "EM Group assure la création, le paramétrage et le suivi de la campagne Meta Ads dans le cadre d'une obligation de moyens. Aucun résultat spécifique (nombre de clics, leads, ventes) n'est garanti.\n\nLe budget publicitaire est entièrement à la charge du client (minimum recommandé : 100€/mois). EM Group ne perçoit aucune commission sur le budget publicitaire.\n\nLe client fournit les accès nécessaires à son Meta Business Manager. EM Group ne crée pas de compte publicitaire au nom du client et ne peut être tenu responsable de toute décision de Meta concernant le compte du client (suspension, restriction, désactivation)." },
  { n: 9, title: "Landing page", body: "La landing page créée dans le cadre de l'offre découverte est hébergée sur l'infrastructure d'EM Group Digital et reste propriété d'EM Group.\n\nElle est mise à disposition du client pendant toute la durée de la relation commerciale.\n\nEn cas d'arrêt de la collaboration, la landing page est désactivée sous 30 jours calendaires.\n\nLes contenus (textes, visuels) sont récupérables par le client sur simple demande écrite. Le code source et l'hébergement restent propriété d'EM Group." },
  { n: 10, title: "Responsabilité", body: "EM Group s'engage à une obligation de moyens dans la réalisation des prestations. EM Group ne saurait être tenu responsable des résultats commerciaux obtenus par le client à l'issue des actions menées.\n\nL'exactitude des informations fournies par le client (textes, visuels, données entreprise) reste sous l'entière responsabilité du client." },
  { n: 11, title: "Disponibilité de l'offre", body: "L'offre découverte est disponible pour un nombre restreint d'entreprises par mois afin de garantir la qualité de l'accompagnement. EM Group se réserve le droit de clôturer les inscriptions à tout moment sans préavis." },
  { n: 12, title: "Droit applicable", body: "Les présentes CGV sont soumises au droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable avant tout recours judiciaire. À défaut d'accord, le tribunal compétent sera celui du siège social d'EM Group Digital." },
];

function CgvAccordionItem({ article, isOpen, onToggle }) {
  return (
    <div className="border-b border-white/5 last:border-b-0" data-testid={`cgv-article-${article.n}`}>
      <button type="button" onClick={onToggle} className="w-full text-left flex items-center gap-3 py-4 hover:text-white transition" aria-expanded={isOpen}>
        <span className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-mono w-12 shrink-0">Art. {article.n}</span>
        <span className="flex-1 text-sm md:text-base font-medium text-white/85">{article.title}</span>
        <ChevronDown size={15} className={`text-white/50 transition-transform shrink-0 ${isOpen ? "rotate-180 text-white" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="pb-5 pl-[68px] pr-4 text-sm text-white/65 leading-relaxed whitespace-pre-line">{article.body}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DecouverteRS() {
  const [openCgv, setOpenCgv] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", sector: SECTORS[0], plan: "pro", website: "", referrer_source: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [directCheckoutLoading, setDirectCheckoutLoading] = useState(null);

  // Checkout Stripe direct depuis une carte (sans capture lead préalable)
  const handleDirectCheckout = async (planKey) => {
    setDirectCheckoutLoading(planKey);
    try {
      const stripePlanId = planKey === "pro" ? "decouverte_pro" : "decouverte_essentiel";
      const { data } = await axios.post(`${API}/checkout/subscription`, {
        plan_id: stripePlanId,
        origin_url: window.location.origin,
      });
      if (data?.url) { window.location.href = data.url; return; }
      setError("Erreur de redirection vers le paiement.");
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de démarrer le paiement. Merci de réessayer.");
    } finally {
      setDirectCheckoutLoading(null);
    }
  };

  useEffect(() => {
    // SEO géré par <SEOHead /> ci-dessous
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.company.trim() || !form.phone.trim() || !form.email.trim()) {
      setError("Merci de remplir tous les champs obligatoires."); return;
    }
    setLoading(true);
    try {
      // 1. Capture du lead (toujours, même si l'utilisateur abandonne Stripe)
      await axios.post(`${API}/leads/decouverte-rs`, form);
      fireLeadConversion(50);

      // 2. Création du Stripe Checkout avec essai de 60 jours
      const stripePlanId = form.plan === "pro" ? "decouverte_pro" : "decouverte_essentiel";
      const { data } = await axios.post(`${API}/checkout/subscription`, {
        plan_id: stripePlanId,
        origin_url: window.location.origin,
        email: form.email,
      });

      if (data?.url) {
        // Redirection vers Stripe Checkout
        window.location.href = data.url;
        return;
      }

      // Fallback : si Stripe ne renvoie pas d'URL, on affiche au moins l'accusé de réception
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || "Une erreur est survenue. Réessayez ou écrivez-nous à contact@emgroupdigital.fr");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden" data-testid="decouverte-rs-page">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        canonical={CANONICAL}
        ogImage="https://emgroupdigital.fr/brand/logo-em-group.png"
        locale="fr_FR"
        htmlLang="fr"
        robots="index, follow"
        localBusiness={LOCAL_BUSINESS}
        serviceSchema={SERVICE_SCHEMA}
        faqSchema={FAQ_ITEMS}
      />
      {/* Ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#E52431]/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#FF6A75]/10 blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-10 pt-6 md:pt-8">
        <Link to="/" data-testid="decouverte-rs-logo" className="inline-flex items-center gap-2 group">
          <img src="/brand/logo-em-group.png" alt="EM Group" className="w-10 h-10 rounded-full" />
          <span className="font-outfit text-base font-medium tracking-tight text-white/90 group-hover:text-white transition">EM Group</span>
        </Link>
      </header>

      <main className="relative z-10 px-6 md:px-10 py-10 md:py-16 max-w-5xl mx-auto">
        {/* HERO */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white text-[10px] uppercase tracking-[0.15em] font-bold shadow-lg shadow-[#E52431]/30">
            <Sparkles size={11} /> Offre découverte — Places limitées
          </div>
          <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75] font-medium">2 mois offerts</span> pour développer<br className="hidden md:block" /> votre visibilité sur les réseaux sociaux.
          </h1>
          <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">Publications + visuels + 1 campagne Meta Ads incluse — <strong className="text-white">sans engagement</strong>.</p>
          <div className="mt-6 inline-flex items-start gap-2.5 px-4 py-3 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25 text-[13px] text-amber-100/85 leading-relaxed">
            <Clock size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <span>Offre limitée — disponible pour un nombre restreint d&apos;entreprises chaque mois, pour garantir un accompagnement de qualité.</span>
          </div>
          {CLIENT_URL && (
            <a href={CLIENT_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-white/55 hover:text-white transition" data-testid="client-link">
              Découvrir notre exemple client <ExternalLink size={11} />
            </a>
          )}
        </motion.section>

        {/* INCLUSIONS */}
        <section className="mt-16 md:mt-20" data-testid="inclusions-section">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">— Inclus pendant 2 mois</div>
          <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">Ce qui est inclus.</h2>
          <ul className="mt-8 space-y-3">
            {INCLUSIONS.map((it, i) => (
              <li key={i} className={`flex items-start gap-3 p-4 rounded-2xl border ${it.ok ? "border-white/10 bg-white/[0.025]" : "border-amber-500/25 bg-amber-500/[0.04]"}`}>
                {it.ok
                  ? <div className="w-6 h-6 shrink-0 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Check size={13} className="text-emerald-300" /></div>
                  : <div className="w-6 h-6 shrink-0 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center"><AlertTriangle size={12} className="text-amber-300" /></div>}
                <span className="text-sm md:text-base text-white/85 leading-relaxed flex-1">{it.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-3xl border border-[#E52431]/30 bg-gradient-to-br from-[#E52431]/[0.08] via-white/[0.02] to-transparent p-6 md:p-7" data-testid="value-block">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#E52431]/15 border border-[#E52431]/40 flex items-center justify-center"><Megaphone size={22} className="text-[#FFB4BB]" /></div>
              <p className="text-base md:text-lg text-white/90 leading-relaxed">Une campagne Meta Ads seule peut valoir <strong>jusqu&apos;à 1 000€</strong> chez certains prestataires. Ici, elle est <strong className="text-[#FFB4BB]">incluse dans votre offre découverte, sans frais supplémentaires.</strong></p>
            </div>
          </div>
        </section>

        {/* VALEUR OFFERTE — récap chiffré */}
        <section className="mt-16 md:mt-20" data-testid="value-recap-section">
          <div className="rounded-3xl border border-[#E52431]/35 bg-gradient-to-br from-[#E52431]/[0.10] via-[#1a0608] to-[#0A0A0E] p-6 md:p-10 relative overflow-hidden" data-testid="value-recap-block">
            <div className="absolute -top-24 -right-16 w-72 h-72 bg-[#E52431]/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB4BB] font-semibold">— Récap valeur</div>
              <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">
                Ce que vous recevez <span className="text-[#FFB4BB]">gratuitement</span>.
              </h2>

              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm" data-testid="value-item-1">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <Check size={18} className="text-emerald-300" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base text-white/85 leading-snug">
                      2 mois de gestion réseaux sociaux <strong className="text-white">Essentiel</strong>
                    </div>
                    <div className="mt-1 font-outfit text-xl md:text-2xl font-medium text-[#FF6A75]">
                      = 900€ de valeur offerte
                    </div>
                  </div>
                </li>

                <li className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm" data-testid="value-item-2">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <Check size={18} className="text-emerald-300" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base text-white/85 leading-snug">
                      1 campagne Meta Ads — <strong className="text-white">setup inclus</strong>
                    </div>
                    <div className="mt-1 font-outfit text-xl md:text-2xl font-medium text-[#FF6A75]">
                      = jusqu&apos;à 1 000€ de valeur sur le marché
                    </div>
                  </div>
                </li>
              </ul>

              <div className="mt-8 flex items-center gap-3 p-5 rounded-2xl bg-gradient-to-r from-[#E52431]/20 via-[#E52431]/10 to-transparent border border-[#E52431]/40" data-testid="value-total">
                <ArrowRight size={22} className="text-[#FFB4BB] shrink-0" />
                <p className="text-base md:text-lg leading-relaxed text-white">
                  Soit <strong className="font-outfit text-2xl md:text-3xl text-[#FF6A75]">jusqu&apos;à 2 000€</strong> de valeur offerte pour démarrer.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* PLANS */}
        <section className="mt-20 md:mt-24" data-testid="plans-section">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">— Suite de l&apos;aventure</div>
            <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">Choisissez votre formule avant de démarrer.</h2>
            <p className="mt-3 text-white/65 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">Votre formule démarre automatiquement après les 2 mois offerts — <strong className="text-white">1 mois d&apos;engagement minimum</strong>, résiliable ensuite à tout moment.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {PLANS.map((p, idx) => (
              <motion.div key={p.key} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: idx * 0.08 }}
                data-testid={`plan-${p.key}`}
                className={`relative rounded-3xl border p-6 md:p-7 backdrop-blur-xl ${p.highlight ? "border-[#E52431]/40 bg-gradient-to-br from-[#E52431]/[0.08] via-white/[0.03] to-transparent shadow-[0_0_60px_-15px_rgba(229,36,49,0.4)]" : "border-white/10 bg-white/[0.025]"}`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white text-[10px] uppercase tracking-[0.15em] font-bold shadow-lg shadow-[#E52431]/30">
                    <Sparkles size={10} className="inline -mt-0.5 mr-1" />{p.badge}
                  </div>
                )}
                <h3 className="font-outfit text-2xl font-light tracking-tight">{p.name}</h3>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className={`font-outfit text-4xl font-medium ${p.highlight ? "text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75]" : "text-white"}`}>{p.price}</span>
                  <span className="text-sm text-white/55">{p.priceUnit}</span>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      {f.ok
                        ? <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                        : <X size={14} className="text-white/25 mt-0.5 shrink-0" />}
                      <span className={f.ok ? "text-white/85" : "text-white/35 line-through"}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => handleDirectCheckout(p.key)}
                  disabled={directCheckoutLoading === p.key}
                  data-testid={`plan-${p.key}-stripe-cta`}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition disabled:opacity-60 ${
                    p.highlight
                      ? "bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.45)]"
                      : "border border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                  }`}
                >
                  {directCheckoutLoading === p.key ? <Loader2 size={14} className="animate-spin" /> : null}
                  {directCheckoutLoading === p.key ? "Redirection…" : "Je démarre gratuitement"}
                  {directCheckoutLoading !== p.key ? <ArrowRight size={14} /> : null}
                </button>
                <p className="mt-3 text-center text-[10px] text-white/45 leading-relaxed">
                  <ShieldCheck size={10} className="inline -mt-0.5 mr-0.5 text-emerald-300" /> Aucun prélèvement pendant 60 jours
                </p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 max-w-2xl mx-auto" data-testid="effective-price-block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold text-center">— Coût réel sur 6 mois</div>
            <p className="mt-3 text-center text-sm md:text-base text-white/75 italic leading-relaxed">
              Avec les 2 mois offerts sur une période de 6 mois :<br />
              → <span className="text-white not-italic font-medium">Essentiel</span> : revient à <strong className="text-[#FFB4BB] not-italic">300€/mois effectif</strong><br />
              → <span className="text-white not-italic font-medium">Pro</span> : revient à <strong className="text-[#FFB4BB] not-italic">500€/mois effectif</strong>
            </p>
          </div>
          <p className="mt-6 text-center text-xs text-white/45 italic max-w-2xl mx-auto leading-relaxed">Passage à la formule supérieure possible à tout moment. Retour à la formule inférieure non possible en cours d&apos;engagement.</p>

          {/* OFFRE PERSONNALISÉE — sous les 2 cartes de formules */}
          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.025] p-6 md:p-8 backdrop-blur-xl max-w-3xl mx-auto" data-testid="custom-offer-block">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-[#E52431]/20 to-[#FF6A75]/10 border border-[#E52431]/30 flex items-center justify-center">
                <Settings2 size={26} className="text-[#FFB4BB]" />
              </div>
              <div className="flex-1">
                <h3 className="font-outfit text-xl md:text-2xl font-light tracking-tight">Vous avez des besoins spécifiques&nbsp;?</h3>
                <p className="mt-2 text-sm md:text-base text-white/65 leading-relaxed">
                  Nous créons une offre sur mesure adaptée à votre activité et vos objectifs.
                </p>
              </div>
              <a
                href={WHATSAPP_CUSTOM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="custom-offer-cta"
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition"
              >
                Discutons de votre projet <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </section>

        {/* FORM */}
        <section className="mt-20 md:mt-24" id="formulaire" data-testid="form-section">
          <div className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold">— Démarrage</div>
            <h2 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight">Je démarre mon offre découverte.</h2>
          </div>
          <div className="max-w-2xl mx-auto rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.02] p-6 md:p-8 backdrop-blur-xl">
            {!submitted ? (
              <form onSubmit={onSubmit} data-testid="decouverte-rs-form" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="form-name" type="text" placeholder="Nom complet *" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" required />
                  <input data-testid="form-company" type="text" placeholder="Nom de l&apos;entreprise *" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="form-phone" type="tel" placeholder="Téléphone *" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" required />
                  <input data-testid="form-email" type="email" placeholder="Email *" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" required />
                </div>
                <select data-testid="form-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#E52431]/60 outline-none">
                  {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="form-website" type="url" placeholder="URL du site web (facultatif)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#E52431]/60 outline-none" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-2">Comment avez-vous entendu parler de nous&nbsp;? <span className="text-white/35 normal-case tracking-normal">(facultatif)</span></div>
                  <select
                    data-testid="form-referrer"
                    value={form.referrer_source}
                    onChange={(e) => setForm({ ...form, referrer_source: e.target.value })}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#E52431]/60 outline-none"
                  >
                    <option value="">— Choisir —</option>
                    {REFERRER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.1em] text-white/55 font-medium mb-2">Formule choisie *</div>
                  <div className="grid grid-cols-2 gap-2">
                    {PLANS.map((p) => {
                      const active = form.plan === p.key;
                      return (
                        <button key={p.key} type="button" onClick={() => setForm({ ...form, plan: p.key })} data-testid={`form-plan-${p.key}`} className={`px-3 py-3 rounded-xl text-sm font-medium transition border text-left ${active ? "bg-[#E52431]/15 border-[#E52431]/50 text-white" : "bg-black/30 border-white/10 text-white/65 hover:bg-black/40"}`}>
                          <div>{p.name}</div>
                          <div className="text-[11px] text-white/45 mt-0.5">{p.price} {p.priceUnit}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {error && <div className="p-3 rounded-xl bg-[#E52431]/10 border border-[#E52431]/30 text-sm text-[#FFB4BB]" data-testid="form-error">{error}</div>}

                {/* Badge value reminder juste au-dessus du CTA */}
                <div className="flex justify-center" data-testid="cta-value-badge">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E52431]/[0.10] border border-[#E52431]/30 text-[11px] uppercase tracking-[0.12em] font-bold text-[#FFB4BB]">
                    <Gift size={11} /> Jusqu&apos;à 2 000€ de valeur offerte
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="form-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white font-semibold px-6 py-4 text-sm uppercase tracking-[0.12em] hover:shadow-[0_0_40px_rgba(229,36,49,0.45)] transition disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? "Redirection sécurisée…" : "Je démarre — 60 jours gratuits"}
                </button>
                <p className="text-[11px] text-white/55 text-center leading-relaxed pt-1">
                  <ShieldCheck size={11} className="inline -mt-0.5 mr-1 text-emerald-300" />
                  Paiement sécurisé par <strong className="text-white/75">Stripe</strong>. Carte requise, <strong className="text-white">aucun prélèvement pendant 60 jours</strong>. Annulation à tout moment depuis votre espace client.
                </p>
                <p className="text-[11px] text-white/45 text-center leading-relaxed pt-1">Offre soumise aux CGV disponibles ci-dessous.</p>
              </form>
            ) : (
              <div className="text-center py-8" data-testid="form-success">
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mx-auto flex items-center justify-center"><Check size={28} className="text-emerald-300" /></div>
                <h3 className="mt-5 font-outfit text-2xl font-light tracking-tight">Demande bien reçue.</h3>
                <p className="mt-2 text-white/65 text-sm leading-relaxed">On vous rappelle <strong className="text-white">sous 24h</strong> pour finaliser ensemble le démarrage.</p>
              </div>
            )}
          </div>

          {/* CONTACT SECONDAIRE — WhatsApp + Instagram */}
          <div className="max-w-2xl mx-auto mt-10 text-center" data-testid="secondary-contact-section">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">— Autres canaux</div>
            <h3 className="mt-2 font-outfit text-xl md:text-2xl font-light tracking-tight">Vous préférez nous contacter d&apos;abord&nbsp;?</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="secondary-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.05] hover:bg-emerald-500/[0.12] text-white px-5 py-3.5 text-sm font-medium transition"
              >
                <MessageCircle size={16} className="text-emerald-300" /> Nous écrire sur WhatsApp
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="secondary-instagram-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E1306C]/35 bg-[#E1306C]/[0.05] hover:bg-[#E1306C]/[0.12] text-white px-5 py-3.5 text-sm font-medium transition"
              >
                <Instagram size={16} className="text-[#F5A6C4]" /> Nous écrire sur Instagram
              </a>
            </div>
            <p className="mt-5 text-xs text-white/45 leading-relaxed">
              <Clock size={11} className="inline -mt-0.5 mr-1" /> Réponse sous 24h — Sans engagement
            </p>
          </div>
        </section>
        <FAQSection
          items={FAQ_ITEMS}
          testIdPrefix="decouverte-rs-faq"
          title="Questions fréquentes sur la gestion des réseaux sociaux."
        />
      </main>

      {/* CGV — fond plus sombre pour distinction */}
      <section className="relative z-10 bg-black/40 border-t border-white/5 mt-20 py-16 px-6 md:px-10" data-testid="cgv-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold text-center">— Cadre légal</div>
          <h2 className="mt-2 text-center font-outfit text-2xl md:text-3xl font-light tracking-tight">Conditions Générales de Vente — Offre Découverte</h2>
          <p className="mt-3 text-center text-xs text-white/50">Cliquez sur un article pour le déplier.</p>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] px-4 md:px-6">
            {CGV_ARTICLES.map((a) => (
              <CgvAccordionItem key={a.n} article={a} isOpen={openCgv === a.n} onToggle={() => setOpenCgv(openCgv === a.n ? null : a.n)} />
            ))}
          </div>
        </div>
      </section>

      <footer className="relative z-10 px-6 md:px-10 py-8 text-[11px] text-white/35 text-center">
        © {new Date().getFullYear()} EM Group Digital — Tous droits réservés | <a href="mailto:contact@emgroupdigital.fr" className="hover:text-white/60 transition">contact@emgroupdigital.fr</a>
      </footer>
    </div>
  );
}
